import { call } from '@advanza/api'
import { Icon } from '@advanza/ui'
import Pagination from 'components/Pagination'
import React, { useEffect, useState } from 'react'

interface DormantRow {
    user_id: number
    name: string
    email: string | null
    country_code: string | null
    last_active: string | null
    days_inactive: number
}

interface DormantResponse {
    status?: string
    items?: DormantRow[]
    count?: number
    dormantDays?: number
    error?: string
}

interface Props {
    refreshKey: number
}

const DEFAULT_PAGE_SIZE = 25
const PAGE_SIZE_OPTIONS = [25, 50, 100]
const DORMANT_DAYS_OPTIONS = [30, 60, 90]

const DemandNativeAppDormantTable = ({ refreshKey }: Props) => {
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [dormantDays, setDormantDays] = useState(30)
    const [items, setItems] = useState<DormantRow[]>([])
    const [count, setCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [previousRefreshKey, setPreviousRefreshKey] = useState(refreshKey)

    useEffect(() => {
        const bypassCache = refreshKey !== previousRefreshKey
        setPreviousRefreshKey(refreshKey)
        loadPage(page, pageSize, dormantDays, bypassCache)
    }, [page, pageSize, dormantDays, refreshKey])

    const loadPage = async (
        nextPage: number,
        nextPageSize: number,
        nextDormantDays: number,
        bypassCache: boolean,
    ) => {
        setLoading(true)
        setError('')
        try {
            const params = new URLSearchParams()
            params.set('page', String(nextPage))
            params.set('pageSize', String(nextPageSize))
            params.set('dormantDays', String(nextDormantDays))
            if (bypassCache) params.set('bypass_cache', '1')
            // Cache-buster: keeps the URL unique so no CDN/browser cache can
            // serve a stale body for this live dashboard.
            params.set('_', String(Date.now()))

            const url = `office/misc/demand-native-app-dormant?${params.toString()}`
            const response = (await call(url)) as DormantResponse
            if (response?.status === 'success' && Array.isArray(response.items)) {
                setItems(response.items)
                setCount(response.count ?? 0)
            } else {
                setError(response?.error || 'Failed to load dormant users')
            }
        } catch (err) {
            setError('Error: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }

    const onPageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(event.target.value) || DEFAULT_PAGE_SIZE)
        setPage(0)
    }

    const onDormantDaysChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setDormantDays(Number(event.target.value) || 30)
        setPage(0)
    }

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-md text-neutral-600">
                    Inactive for at least
                    <select
                        value={dormantDays}
                        onChange={onDormantDaysChange}
                        className="appearance-none rounded-md border border-neutral-300 bg-white px-2 py-1 text-md cursor-pointer hover:border-neutral-400 focus:outline-none focus:ring-1 focus:ring-blue-500">
                        {DORMANT_DAYS_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                                {opt} days
                            </option>
                        ))}
                    </select>
                </label>
                <p className="text-md text-neutral-500">
                    Native users with no app open in this window, measured from their last open (or
                    their install date if they never opened the app).
                </p>
            </div>

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-md">
                    {error}
                </div>
            )}

            <div className="relative max-h-[480px] overflow-y-auto overflow-x-auto rounded-lg border border-neutral-200">
                {loading && (
                    <div className="absolute inset-x-0 top-0 z-20 h-1 overflow-hidden bg-blue-100">
                        <div className="h-full w-1/3 animate-pulse bg-blue-500" />
                    </div>
                )}
                <table className="min-w-full divide-y divide-neutral-200 text-md">
                    <thead className="bg-neutral-50 sticky top-0 z-10">
                        <tr>
                            <th className="px-4 py-3 text-left font-semibold text-neutral-700 whitespace-nowrap">
                                User
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-neutral-700 whitespace-nowrap">
                                Email
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-neutral-700 whitespace-nowrap">
                                Country
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-neutral-700 whitespace-nowrap">
                                Last active
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-neutral-700 whitespace-nowrap">
                                Days inactive
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 bg-white">
                        {items.length === 0 && !loading && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="px-4 py-6 text-center text-md text-neutral-500">
                                    No dormant users found
                                </td>
                            </tr>
                        )}
                        {items.map((m) => (
                            <tr key={m.user_id} className="hover:bg-neutral-50/80">
                                <td className="px-4 py-3 font-medium text-neutral-900 whitespace-nowrap">
                                    {m.name}
                                </td>
                                <td className="px-4 py-3 text-neutral-700 whitespace-nowrap">
                                    {m.email || '–'}
                                </td>
                                <td className="px-4 py-3 text-center text-neutral-700">
                                    {m.country_code || '–'}
                                </td>
                                <td className="px-4 py-3 text-center text-neutral-700 whitespace-nowrap">
                                    {m.last_active
                                        ? new Date(m.last_active).toLocaleDateString()
                                        : '–'}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                    {m.days_inactive}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-md text-neutral-600">
                    Rows per page
                    <span className="relative inline-flex items-center">
                        <Icon
                            name="keyboard_arrow_down"
                            fontSize={18}
                            className="pointer-events-none absolute left-1 text-neutral-500"
                        />
                        <select
                            value={pageSize}
                            onChange={onPageSizeChange}
                            className="appearance-none rounded-md border border-neutral-300 bg-white pl-7 pr-2 py-1 text-md cursor-pointer hover:border-neutral-400 focus:outline-none focus:ring-1 focus:ring-blue-500">
                            {PAGE_SIZE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    </span>
                </label>
                <div className="min-w-[160px]">
                    <Pagination
                        current={page}
                        count={count}
                        pageSize={pageSize}
                        onPageChange={setPage}
                    />
                </div>
            </div>
        </div>
    )
}

export default DemandNativeAppDormantTable
